import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  ColumnFiltersState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import posthog from "posthog-js";
import { ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Table } from "@/components/ui/table";
import { Campaign, Creator, StatusValue } from "@/types/types";
import {
  useCampaignStore,
  useCreatorsStore,
  useOutreachStore,
  useDashboardStore,
} from "@/stores";
import { resolveStatusName } from "@/constants/statuses";
import { CreatorsTableProvider } from "./CreatorsTableContext";
import { CreatorsTableProps } from "./types";
import { useCreatorColumns, useStatusColumnSize } from "./columns/useCreatorColumns";
import { unionCampaignFields } from "./columns/unionFields";
import { globalFilterFn } from "./filters/filterFunctions";
import {
  SearchFilter,
  StatusFilter,
  IssuesFilter,
  ColumnFilter,
  ColumnVisibility,
} from "./filters";
import {
  DeleteCreatorsDialog,
  TransferCreatorsDialog,
  ExportCreatorsDialog,
  StartCampaignAction,
  ActivateCreatorDialog,
} from "./actions";
import CreatorsTableHeader from "./TableHeader";
import CreatorsTableBody from "./TableBody";
import TablePagination from "./TablePagination";
import CreatorsTableSkeleton from "./TableSkeleton";
import FetchProgress from "./FetchProgress";
import CreatorRightPanel from "./CreatorRightPanel";
import PhaseDetailDialog from "./phase/PhaseDetailDialog";
import ShareCreatorRightPanel from "@/components/share/ShareCreatorRightPanel";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

// Columns hidden until the user toggles them in View — per table flavor, the
// org-wide table remembers its own choices.
const CAMPAIGN_VISIBILITY_STORAGE_KEY = "creators_table_column_visibility";
const ORG_WIDE_VISIBILITY_STORAGE_KEY = "global_crm_column_visibility";
const DEFAULT_HIDDEN_COLUMNS: VisibilityState = {
  issues: false,
  engagement_rate: false,
  country: false,
  gender: false,
  age: false,
  language: false,
};

export default function CreatorsTable({
  data,
  setData,
  readOnly = false,
  showStats = false,
  showFields = false,
  showConversations = false,
  shareToken,
  server,
}: CreatorsTableProps) {
  const id = useId();
  // Org-wide (Global CRM) mode: `data` is one server page and every filter /
  // sort / page change is a query the caller runs (see CreatorsTableServer).
  const orgWide = !!server;
  const serverRef = useRef(server);
  serverRef.current = server;
  const [searchParams, setSearchParams] = useSearchParams();
  const [globalFilter, setGlobalFilter] = useState<string>(
    () => searchParams.get("query") ?? ""
  );
  // The `query` param the table itself last wrote (or read): an incoming
  // URL that differs is a deep link to apply; one that matches is our own
  // debounced write echoing back and must not clobber what's being typed.
  const lastQueryParamRef = useRef(searchParams.get("query") ?? "");
  const [selectedCreatorId, setSelectedCreatorId] = useState<
    string | undefined
  >(undefined);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      return [{ id: "status", value: statusParam.split(",") }];
    }
    return [];
  });
  // react-router hands out a NEW setSearchParams on every navigation; going
  // through a ref keeps selectCreator (and therefore the memoized context)
  // stable, so the router-driven re-render of a panel open doesn't re-render
  // every cell. Cells call this instead of subscribing to the router.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  const visibilityStorageKey = orgWide
    ? ORG_WIDE_VISIBILITY_STORAGE_KEY
    : CAMPAIGN_VISIBILITY_STORAGE_KEY;
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    try {
      const stored = localStorage.getItem(visibilityStorageKey);
      if (stored) return JSON.parse(stored);
    } catch {
      // ignore
    }
    return { ...DEFAULT_HIDDEN_COLUMNS };
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        visibilityStorageKey,
        JSON.stringify(columnVisibility)
      );
    } catch {
      // ignore
    }
  }, [columnVisibility, visibilityStorageKey]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // autoResetPageIndex is off (so field edits don't snap to page 1), so reset
  // manually when the row set actually changes — search or column filters.
  useEffect(() => {
    setPagination((p) => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));
  }, [globalFilter, columnFilters, sorting]);
  const [loadingStatusCreators, setLoadingStatusCreators] = useState<
    Record<string, string>
  >({});
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [approvalDialogCreatorId, setApprovalDialogCreatorId] = useState<string | null>(null);
  const [rejectionPopoverCreatorId, setRejectionPopoverCreatorId] = useState<string | null>(null);
  const [phaseDialog, setPhaseDialog] = useState<{ creatorId: string; phaseKey: string } | null>(null);

  const { campaignId: storeCampaignId, campaign: storeCampaign } = useCampaignStore();
  // The campaign store is the OPEN campaign — meaningless org-wide, where
  // rows resolve their own campaign below.
  const campaignId = orgWide ? null : storeCampaignId;
  const campaign = orgWide ? null : storeCampaign;
  const { startInitialOutreach } = useOutreachStore();
  const { fetchCreators, deleteCreators, transferCreators } = useCreatorsStore();
  const { campaigns: orgCampaigns } = useDashboardStore();
  const { getToken, orgId } = useAuth();
  // One router subscription here instead of one per status cell: a
  // component that calls useNavigate re-renders on every navigation.
  const navigate = useNavigate();
  const openTask = useCallback(
    (creatorId: string) => navigate(`/dashboard/tasks?action=${creatorId}`),
    [navigate]
  );

  // Every campaign rows may belong to: the one open campaign, or org-wide
  // the whole org (from the server response, so it carries fields, tiers,
  // stages, pipeline and the sending inbox).
  const campaigns: Campaign[] = useMemo(
    () => (orgWide ? server.campaigns : campaign ? [campaign] : []),
    [orgWide, server?.campaigns, campaign]
  );
  const campaignsById = useMemo(
    () => new Map(campaigns.map((c) => [c._id, c])),
    [campaigns]
  );
  const campaignFor = useCallback(
    (creator: Creator | null | undefined): Campaign | null => {
      if (!orgWide) return campaign;
      const rowCampaignId = creator?.campaign_id;
      return rowCampaignId ? campaignsById.get(rowCampaignId) ?? null : null;
    },
    [orgWide, campaign, campaignsById]
  );
  const statusLabel = useCallback(
    (status: string) => resolveStatusName(status, campaigns),
    [campaigns]
  );
  const unionFields = useMemo(() => unionCampaignFields(campaigns), [campaigns]);

  // Cleanup stale portals on mount and when selectedCreatorId changes
  useEffect(() => {
    const cleanupStalePortals = () => {
      const portalContainers = document.querySelectorAll("[data-radix-portal]");
      portalContainers.forEach((container) => {
        if (container.parentElement && !container.hasChildNodes()) {
          container.parentElement.removeChild(container);
        }
      });
    };
    cleanupStalePortals();
  }, [selectedCreatorId]);

  // Sync URL params when they change
  useEffect(() => {
    const queryParam = searchParams.get("query") ?? "";
    if (queryParam !== lastQueryParamRef.current) {
      lastQueryParamRef.current = queryParam;
      setGlobalFilter(queryParam);
    }

    const creatorIdParam = searchParams.get("creatorId");
    if (creatorIdParam) {
      setSelectedCreatorId(creatorIdParam);
    }
  }, [searchParams]);

  // Search text → ?query=, debounced and replacing (typing is not history),
  // keeping the other params. One navigation per pause instead of one per
  // keystroke — each navigation re-renders the whole route tree.
  useEffect(() => {
    if (globalFilter === lastQueryParamRef.current) return;
    const timer = window.setTimeout(() => {
      lastQueryParamRef.current = globalFilter;
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (globalFilter) next.set("query", globalFilter);
          else next.delete("query");
          return next;
        },
        { replace: true }
      );
    }, 300);
    return () => window.clearTimeout(timer);
  }, [globalFilter]);

  // Sync selectedCreatorId with URL params
  useEffect(() => {
    const creatorIdParam = searchParams.get("creatorId") || undefined;
    setSelectedCreatorId((prev) => {
      if (prev !== creatorIdParam) {
        return creatorIdParam;
      }
      return prev;
    });
  }, [searchParams]);

  // Sync status column filter to URL — only when it actually differs: the
  // functional setter navigates even when it returns prev, and every
  // navigation re-renders the whole route tree.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  useEffect(() => {
    const statusFilter = columnFilters.find((f) => f.id === "status");
    const statusValues = (statusFilter?.value as string[] | undefined) ?? [];
    const nextValue = statusValues.length ? statusValues.join(",") : null;
    if (searchParamsRef.current.get("status") === nextValue) return;

    setSearchParamsRef.current(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (nextValue) {
          newParams.set("status", nextValue);
        } else {
          newParams.delete("status");
        }
        return newParams;
      },
      { replace: true }
    );
  }, [columnFilters]);

  // Public share + conversations: open the first creator's conversation by default
  // (once — closing it should not re-open it). Write the creatorId to the URL so
  // the URL-sync effects above are the single source of truth (otherwise they
  // clobber a directly-set selection, e.g. under StrictMode's double-invoke).
  const didAutoSelectConvoRef = useRef(false);
  useEffect(() => {
    if (!readOnly || !showConversations || didAutoSelectConvoRef.current) return;
    if (selectedCreatorId || searchParams.get("creatorId")) {
      didAutoSelectConvoRef.current = true;
      return;
    }
    const firstWithThread = data.find((c) => c.thread_id);
    if (firstWithThread) {
      didAutoSelectConvoRef.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("creatorId", firstWithThread._id);
          return next;
        },
        { replace: true }
      );
    }
  }, [readOnly, showConversations, data, selectedCreatorId, searchParams, setSearchParams]);

  // (setSearchParamsRef is declared above, next to the URL-sync effects.)
  const selectCreator = useCallback((creatorId: string) => {
    setSelectedCreatorId(creatorId);
    setSearchParamsRef.current((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("creatorId", creatorId);
      return newParams;
    });
  }, []);

  const handleCloseInbox = () => {
    setSelectedCreatorId(undefined);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("creatorId");
      return newParams;
    });
  };

  const handleRefresh = useCallback(async () => {
    if (orgWide) {
      await serverRef.current?.refetch();
      return;
    }
    const token = await getToken();
    if (!campaignId || !token) return;
    fetchCreators(campaignId, token);
  }, [orgWide, getToken, campaignId, fetchCreators]);

  const columns = useCreatorColumns({
    onRefresh: handleRefresh,
    campaign,
    campaigns,
    campaignFor,
    orgWide,
    readOnly,
    showStats,
    showFields,
    showConversations,
  });

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => row._id,
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    // Org-wide the server owns filtering, sorting and paging; the table only
    // holds the state and the current page.
    manualFiltering: orgWide,
    manualSorting: orgWide,
    manualPagination: orgWide,
    rowCount: orgWide ? server.rowCount : undefined,
    state: {
      sorting,
      globalFilter,
      pagination,
      columnFilters,
      columnVisibility,
    },
  });

  // Data-driven Status column width, applied as sizing STATE so the column
  // definitions (and every mounted cell) survive a page flip untouched.
  const statusColumnSize = useStatusColumnSize(data, campaignFor);
  useEffect(() => {
    table.setColumnSizing((prev) =>
      prev.status === statusColumnSize ? prev : { ...prev, status: statusColumnSize }
    );
  }, [statusColumnSize, table]);

  // Org-wide: hand every query change to the caller (it debounces + fetches)
  // and drop the selection — rows about to leave the page can't stay
  // selected invisibly. (Only when there is one: resetting an empty
  // selection still pushes a table state update = one more full render.)
  useEffect(() => {
    if (!orgWide) return;
    serverRef.current?.onQueryChange({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sorting,
      columnFilters,
      globalFilter,
    });
    if (Object.keys(table.getState().rowSelection).length > 0) {
      table.resetRowSelection();
    }
  }, [orgWide, pagination, sorting, columnFilters, globalFilter, table]);

  // Columns that declare themselves default-hidden (org-wide phase / stage
  // extras) start hidden the first time they appear; a stored choice wins.
  useEffect(() => {
    const hidden: VisibilityState = {};
    for (const column of table.getAllLeafColumns()) {
      if (column.columnDef.meta?.defaultHidden && !(column.id in columnVisibility)) {
        hidden[column.id] = false;
      }
    }
    if (Object.keys(hidden).length > 0) {
      setColumnVisibility((prev) => ({ ...prev, ...hidden }));
    }
  }, [columns, columnVisibility, table]);

  const handleDeleteCreators = useCallback(async () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const token = await getToken();
    try {
      if (token) {
        await deleteCreators(
          selectedRows.map((row) => row.original._id),
          token
        );
      }
      table.resetRowSelection();
      // The store dropped the rows locally; org-wide the count and the page
      // come from the server, so re-run the query.
      if (orgWide) await serverRef.current?.refetch();
    } catch (error) {
      console.error("Error updating creator status:", error);
    }
  }, [table, getToken, deleteCreators, orgWide]);

  // Transfer is allowed for creators that haven't reached a decision yet
  // (pending / to-contact / outreached / negotiating) into a same-platform
  // campaign in Search (they land as pending) or already running (they land
  // as to-contact). Amy-run campaigns qualify on both ends — the backend
  // resets the creator to a clean slate the eve engine picks up natively.
  // Org-wide the selection may span campaigns: they must share a platform,
  // and none of their own campaigns is offered as a target.
  const selectedRows = table.getSelectedRowModel().rows;
  const selectedCount = selectedRows.length;
  // The selection strip animates closed AFTER the selection empties — freeze
  // the last non-zero count so its contents don't flash "0 selected" (and the
  // count-gated dialog buttons don't unmount) mid-exit-tween.
  const lastSelectedCountRef = useRef(1);
  useEffect(() => {
    if (selectedCount > 0) lastSelectedCountRef.current = selectedCount;
  }, [selectedCount]);
  const stripCount = selectedCount > 0 ? selectedCount : lastSelectedCountRef.current;
  const transferableStatuses = ["pending", "to-contact", "outreached", "negotiating"];
  const allSelectedTransferable =
    selectedRows.length > 0 &&
    selectedRows.every((row) =>
      transferableStatuses.includes(row.original.status ?? "")
    );
  const selectedCampaigns = selectedRows.map((row) => campaignFor(row.original));
  const selectedPlatform = selectedCampaigns[0]?.platform;
  const selectedSharePlatform =
    !!selectedPlatform && selectedCampaigns.every((c) => c?.platform === selectedPlatform);
  const selectedCampaignIds = new Set(selectedCampaigns.map((c) => c?._id));
  const transferTargets = orgCampaigns.filter(
    (c) =>
      c.platform === selectedPlatform &&
      ["SEARCH", "OUTREACH"].includes(String(c.status).toUpperCase()) &&
      !selectedCampaignIds.has(c._id)
  );

  const handleTransferCreators = async (targetCampaignId: string) => {
    const token = await getToken();
    if (!token || !orgId) {
      console.error("Missing token or organization id");
      return;
    }
    try {
      await transferCreators(
        selectedRows.map((row) => row.original._id),
        targetCampaignId,
        orgId,
        token
      );
      table.resetRowSelection();
      // The store only re-reads the OPEN campaign's roster; org-wide the
      // moved rows changed campaign, so re-run the query.
      if (orgWide) await serverRef.current?.refetch();
    } catch (error) {
      console.error("Error transferring creators:", error);
    }
  };

  async function handleStartInitialOutreach({
    refreshCreatorData,
  }: {
    refreshCreatorData: boolean;
  }) {
    const token = await getToken();
    if (!token || !campaignId) {
      console.error("No token found");
      return;
    }

    setOutreachLoading(true);

    try {
      const updatedData = data.map((creator) => ({
        ...creator,
        status:
          creator.status === "to-contact"
            ? ("outreach" as StatusValue)
            : creator.status,
      }));

      setData(updatedData);

      // ONE launch door for every campaign, Amy-run or not: the backend arms
      // Amy-run campaigns for her itself and emails nothing for them. With
      // the refresh switch on it only queues the creator refresh — the
      // maintenance worker launches the campaign when that finishes.
      await startInitialOutreach(campaignId, token, refreshCreatorData);

      posthog.capture("start_campaign_outreach", {
        campaignId,
        engine: campaign?.is_eve ? "amy" : "legacy",
        refresh_creator_data: refreshCreatorData,
      });

      if (refreshCreatorData) {
        // Show the refresh at once: the launch button turns into the progress
        // chip, which then polls the real record (StartCampaignAction).
        useDashboardStore.setState((state) => ({
          campaigns: state.campaigns.map((c) =>
            c._id === campaignId
              ? {
                  ...c,
                  creator_refresh: {
                    status: "running",
                    started_at: new Date().toISOString(),
                  },
                }
              : c
          ),
        }));
      }

      await fetchCreators(campaignId, token);
    } catch (error) {
      console.error("Error starting outreach sequence:", error);
      await fetchCreators(campaignId, token);
    } finally {
      setOutreachLoading(false);
    }
  }

  const serverFacets = orgWide ? server.facets : null;
  const serverLoading = orgWide ? server.loading : false;
  // First page still in flight: the whole shell is a skeleton (the hooks
  // above keep running — the query effect is what triggers that first fetch).
  const initialLoading = orgWide ? !!server.initialLoading : false;

  // Two memoized context values (see CreatorsTableContext): the API/scope
  // one changes rarely; the UI-state one changes on every keystroke, panel
  // open or loading flip — and only the few components that show those
  // subscribe to it, so the hundreds of memoized cells stay put.
  const contextValue = useMemo(
    () => ({
      table,
      setSelectedCreatorId,
      selectCreator,
      setLoadingStatusCreators,
      setGlobalFilter,
      handleDeleteCreators,
      setApprovalDialogCreatorId,
      setRejectionPopoverCreatorId,
      onRefresh: handleRefresh,
      setPhaseDialog,
      openTask,
      orgWide,
      campaigns,
      campaignFor,
      statusLabel,
      serverFacets,
    }),
    [
      table,
      selectCreator,
      handleDeleteCreators,
      handleRefresh,
      openTask,
      orgWide,
      campaigns,
      campaignFor,
      statusLabel,
      serverFacets,
    ]
  );
  const stateValue = useMemo(
    () => ({
      selectedCreatorId,
      loadingStatusCreators,
      globalFilter,
      approvalDialogCreatorId,
      rejectionPopoverCreatorId,
      phaseDialog,
      serverLoading,
    }),
    [
      selectedCreatorId,
      loadingStatusCreators,
      globalFilter,
      approvalDialogCreatorId,
      rejectionPopoverCreatorId,
      phaseDialog,
      serverLoading,
    ]
  );

  return (
    <CreatorsTableProvider value={contextValue} state={stateValue}>
      <ResizablePanelGroup direction="horizontal" className="h-full">
        <ResizablePanel defaultSize={55} minSize={30}>
          {initialLoading ? (
            <CreatorsTableSkeleton />
          ) : (
          /* Flat inbox-tab shell: no frame — toolbar / rows / pagination
              stacked full-bleed, separated by straight borders. */
          <div className="flex h-full flex-col bg-background">
            {!readOnly && (
              <>
                <div className="flex h-11 shrink-0 items-stretch border-b">
                  {/* Search is its own segment, split from the controls. */}
                  <SearchFilter id={id} />
                  <div className="flex items-center border-l px-2 gap-2">
                    <StatusFilter id={id} />
                    <IssuesFilter id={id} data={data} />
                    <ColumnVisibility />
                    <Separator orientation="vertical" className="h-5" />
                    <ColumnFilter />
                    <Separator orientation="vertical" className="h-5" />
                    {columnFilters.length > 0 && (
                      <Button
                        variant="destructive-ghost"
                        onClick={() => setColumnFilters([])}
                        className="h-7.5 px-2 [&_svg]:opacity-60 hover:[&_svg]:opacity-100 gap-1"
                        size="sm"
                      >
                        <XIcon aria-hidden="true" />
                        Clear
                      </Button>
                    )}
                    <ActivateCreatorDialog />
                    <PhaseDetailDialog />
                  </div>
                  {!orgWide && campaignId && (
                    <StartCampaignAction
                      data={data}
                      campaignId={campaignId}
                      outreachLoading={outreachLoading}
                      onStartOutreach={handleStartInitialOutreach}
                    />
                  )}
                </div>

                {/* Selection actions */}
                <div
                  inert={selectedCount === 0}
                  className={cn(
                    "t-resize flex shrink-0 flex-col justify-end overflow-hidden",
                    selectedCount > 0 ? "h-11" : "h-0"
                  )}
                >
                  <div className="flex h-11 shrink-0 items-center gap-2 border-b px-2">
                    <span className="px-1 text-xs font-mono tabular-nums text-muted-foreground border-r pr-3 border-dashed">
                      {stripCount} selected
                    </span>
                    <DeleteCreatorsDialog
                      selectedCount={stripCount}
                      onDelete={handleDeleteCreators}
                    />
                    <TransferCreatorsDialog
                      selectedCount={stripCount}
                      eligible={
                        allSelectedTransferable &&
                        selectedSharePlatform &&
                        transferTargets.length > 0
                      }
                      campaigns={transferTargets}
                      onTransfer={handleTransferCreators}
                    />
                    <ExportCreatorsDialog
                      selectedCount={stripCount}
                      creators={selectedRows.map((row) => row.original)}
                      campaignFields={orgWide ? unionFields : campaign?.fields}
                    />
                    <Separator orientation="vertical" className="h-5" />
                    <Button
                      onClick={() => table.resetRowSelection()}
                      variant="ghost"
                      size="sm"
                      className="h-7.5 px-2 gap-1 [&_svg]:opacity-60 hover:[&_svg]:opacity-100"
                    >
                      <XIcon aria-hidden="true" />
                      Deselect All
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Rows fill the rest; vertical scroll lives on the
                table-container itself so the sticky header anchors to it
                (scrollbar-color inherits). */}
            <div className="relative flex-1 min-h-0 overflow-hidden [scrollbar-color:--alpha(var(--foreground)/30%)_transparent] **:data-[slot=table-container]:h-full **:data-[slot=table-container]:overflow-y-auto">
              {/* Later fetches (next page, a filter, a refresh): a thin line
                  along the top edge that fills as the request runs and
                  completes when it lands; the current rows stay readable. */}
              {orgWide && <FetchProgress active={serverLoading} />}
              <Table className="table-fixed">
                <CreatorsTableHeader
                  columns={columns}
                  sorting={sorting}
                  columnVisibility={columnVisibility}
                  columnSizing={table.getState().columnSizing}
                />
                <CreatorsTableBody columnsLength={columns.length} />
              </Table>
            </div>

            <div className="shrink-0 p-2 pl-3 text-xs text-muted-foreground">
              <TablePagination id={id} />
            </div>
          </div>
          )}
        </ResizablePanel>

        {!readOnly && (
          <CreatorRightPanel
            selectedCreatorId={selectedCreatorId}
            onClose={handleCloseInbox}
            campaignId={campaignId || ""}
          />
        )}
        {readOnly && showConversations && shareToken && (
          <ShareCreatorRightPanel
            selectedCreatorId={selectedCreatorId}
            onClose={handleCloseInbox}
            token={shareToken}
          />
        )}
      </ResizablePanelGroup>
    </CreatorsTableProvider>
  );
}
